import type { DocumentExtractionRequest, DocumentExtractionResult, DocumentExtractionService } from '@confer/lib'
import type { Auth } from 'firebase/auth'

const failure = () => new Error('The files could not be processed. Please try again or enter the answers manually.')
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value)

export function readDocumentResult(report: unknown, request: DocumentExtractionRequest): DocumentExtractionResult {
  if (!object(report) || report.status !== 'completed' || !object(report.cleanup) || report.cleanup.status !== 'deleted' ||
    !object(report.profile) || report.profile.id !== request.profile || report.profile.questionId !== request.questionId ||
    !Number.isInteger(report.profile.decimalPlaces) || (report.profile.decimalPlaces as number) < 0 ||
    (report.profile.decimalPlaces as number) > 8 || !Array.isArray(report.answers)) throw failure()
  const result: DocumentExtractionResult = { suggestions: [], unresolved: [] }
  const seen = new Set<string>()
  for (const answer of report.answers) {
    if (!object(answer) || answer.questionId !== request.questionId || typeof answer.inputId !== 'string' ||
      seen.has(answer.inputId) || typeof answer.explanation !== 'string') throw failure()
    seen.add(answer.inputId)
    if (['not_found', 'not_applicable', 'needs_clarification'].includes(String(answer.status))) {
      if (answer.value !== null) throw failure()
      result.unresolved.push({ inputId: answer.inputId, explanation: answer.explanation })
      continue
    }
    if (!['reported', 'calculated'].includes(String(answer.status)) || typeof answer.value !== 'number' ||
      !Number.isFinite(answer.value) || !Array.isArray(answer.sources) || !answer.sources.length) throw failure()
    const sources = answer.sources.map((source: unknown) => {
      if (!object(source) || !request.files.some(f => f.name === source.filename) ||
        typeof source.filename !== 'string' || typeof source.location !== 'string' ||
        !source.location.trim() || typeof source.excerpt !== 'string') throw failure()
      return { filename: source.filename, location: source.location, excerpt: source.excerpt }
    })
    result.suggestions.push({ inputId: answer.inputId, value: answer.value,
      decimalPlaces: report.profile.decimalPlaces as number, explanation: answer.explanation, sources })
  }
  return result
}

const encodeFile = (file: File): Promise<{ filename: string; contentBase64: string }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('A file could not be read. Please select it again.'))
    reader.onload = () => resolve({ filename: file.name, contentBase64: String(reader.result).split(',')[1] })
    reader.readAsDataURL(file)
  })

export class FirebaseDocumentExtractionService implements DocumentExtractionService {
  readonly limits = { extensions: ['.xlsx', '.docx'], maxFiles: 8, maxBytes: 5 * 1024 * 1024 }

  constructor(private auth: Auth, private endpoint: string) {}

  async extract(request: DocumentExtractionRequest, { signal }: { signal: AbortSignal }): Promise<DocumentExtractionResult> {
    const user = this.auth.currentUser
    if (!user) throw new Error('Please wait for the survey to connect, then try again.')
    const controller = new AbortController()
    const abort = () => controller.abort()
    signal.addEventListener('abort', abort, { once: true })
    if (signal.aborted) controller.abort()
    const timer = window.setTimeout(abort, 690000)
    try {
      const [token, files] = await Promise.all([user.getIdToken(), Promise.all(request.files.map(encodeFile))])
      const response = await fetch(this.endpoint, {
        method: 'POST', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...request, files, runId: `question-${crypto.randomUUID()}` }),
      })
      if (!response.ok) throw failure()
      return readDocumentResult(await response.json(), request)
    } catch (error) {
      if (controller.signal.aborted && !signal.aborted) throw new Error('Processing timed out. Please try again or enter the answers manually.')
      throw error
    } finally {
      clearTimeout(timer)
      signal.removeEventListener('abort', abort)
    }
  }
}
